/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useState, useEffect, useContext } from 'react'
import { Segment, Image, Header, Icon, Popup, Button, Tab, Label } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { ProductSyncState } from '../ProductSyncState/ProductSyncState'
import ProductDetailsModifiers from './ProductDetailsModifiers'
import { UltyModalContext } from '../../Shared/UltyModal/UltyModalContext'
import { archive, isItemProvidedByUserProvider } from '../../../services/Item/Item.service'
import { getProduct } from '../../../services/Product/Product.service'
import useUser from '../../Shared/UserProvider/useUser'
import { useNavigate, useParams, Link } from 'react-router-dom-v5-compat'
import { UltyInputText } from '@ulty/ulty-ui'
import ItemAvailabilitySetter from '../../Items/ItemAvailabilitySetter'
import useBreadcrumb from '../../NavBar/useBreadcrumb'
import ItemCategories from '../../Items/ItemCategories'
import { isProviderAPos } from '../../../services/Provider/Provider.service'
import { ProductPrices } from '../ProductPrices/ProductPrices'
import { ultyToast } from '../../Shared/UltyToast/UltyToast'
import { useDeviceMediaQuery } from '../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { validate } from 'barcoder'
import Barcode from 'react-barcode'
import { CountriesOfOriginLabel } from '../../Items/CountriesOfOrigin/CountriesOfOriginLabel'

const ProductDetails = () => {
    const [t] = useTranslation()
    const {user, can} = useUser()
    const {setPaths} = useBreadcrumb()
    const {isMobile} = useDeviceMediaQuery()

    const params = useParams()
    const navigate = useNavigate()

    const [canEditItem, setCanEditItem] = useState(true)
    const [canDeleteItem, setCanDeleteItem] = useState(true)
    const [loading, setLoading] = useState(true)
    const [product, setProduct] = useState({
        basePrice: 0,
        taxRate: 0,
        providerIds: [],
        countriesOfOrigin: [],
        inventory: {
            quantity: 0,
            sellPrice: '',
            unavailableUntil: null,
            externalId: '',
            countriesOfOrigin: []
        },
        extraFields: [],
        images: [],
        object: {
            barcode: '',
            name: '',
            description: '',
            overrides: {},
        },
    })
    const {handleUltyModal} = useContext(UltyModalContext)

    const loadProduct = async () => {
        setLoading(true)

        try {
            const p = await getProduct(params.id)
            setProduct(p)
            setCanEditItem(isItemProvidedByUserProvider(p, user) && can('UPDATE', 'product'))
            setCanDeleteItem(isItemProvidedByUserProvider(p, user) && can('UPDATE', 'product'))
        } catch (e) {
            ultyToast({type: 'error', title: `${t(`global.${e.message}`)}`})
            navigateToProductsList()
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        setPaths([{
            text: t('breadcrumb.home'),
            link: true,
            path: '/',
        }, {
            text: t('breadcrumb.catalog'),
            link: true,
            path: '/products',
        }, {
            text: product?.object?.name || t('global.loading'),
            link: false,
            path: '/products',
        }])
    }, [product])

    useEffect(() => {
        loadProduct()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDeleteItem = async () => {
        const modalSettings = {
            title: `${t('product_details.deleteConfirm.title')} ${product?.object?.name} ?`,
            content: <>
                <p>{t('product_details.deleteConfirm.message')}</p>
            </>,
            onValidate: async () => {
                await archive(product.id, {archived: true})
                handleUltyModal(false)
                navigateToProductsList()
            },
        }
        handleUltyModal(true, modalSettings)
    }

    const navigateToProductsList = () => {
        navigate('/products')
    }

    const itemHasTags = product.containsAlcohol ||
      product.countriesOfOrigin?.length > 0 ||
      product.inventory?.countriesOfOrigin?.length > 0

    return (
      <>
          <Segment loading={loading}>
              <div css={css`
                  display: flex;
                  justify-content: flex-end;
                  align-items: center;
                  gap: .5rem;
                  margin-bottom: 1rem;
                  flex-wrap: wrap;
              `}>
                  {canDeleteItem && (
                    <Button
                      type="button"
                      color="red"
                      size="small"
                      onClick={handleDeleteItem}
                      loading={loading}
                    >
                        <Icon name={'delete'}/>
                        {t('global.delete')}
                    </Button>
                  )}

                  {product?.id && product?.inventory && isProviderAPos(user.provider) && (
                    <ItemAvailabilitySetter itemId={product.id}/>
                  )}

                  <Button
                    icon="pencil"
                    as={Link}
                    to={`/products/edit/${product.id}`}
                  />
              </div>

              <div>
                  <div css={css`
                      display: grid;
                      grid-template-areas: ${isMobile ? (`
                              'name'
                              'image'
                              ${product.object.description ? `'description'` : ''}
                              ${itemHasTags ? `'tags'` : ''}
                              'pricing'
                              'identifiers'
                              `)
                              : (`
                              'image name name'
                              ${product.object.description ? `'image description description'` : ''}
                              ${itemHasTags ? `'image tags tags'` : ''}
                              'image pricing identifiers'`
                              )};

                      grid-template-columns: ${isMobile ? '1fr' : 'auto auto 1fr'};
                      grid-template-rows: ${isMobile ?
                              `auto auto ${product.object.description ? 'auto' : ''} ${itemHasTags ? '30px' : ''} auto auto` :
                              `min-content ${product.object.description ? 'auto' : ''} ${itemHasTags ? '30px' : ''} 1fr`
                      };
                      gap: 1rem;
                  `}>
                      <div css={css`
                          grid-area: image;
                          text-align: center;
                          align-self: start;
                      `}>
                          <Image
                            src={product.images.find(image => image.isDefault)?.url}
                            size="medium"
                            css={css`
                                max-height: 300px;
                                max-width: 300px;
                                width: auto;
                                margin: auto;
                                object-fit: contain;
                            `}
                          />
                      </div>

                      <div css={css`
                          grid-area: name;
                          display: flex;
                          align-items: start;
                          gap: 1rem;
                      `}>
                          <Header as="h2">
                              {product?.object?.name}
                          </Header>
                          {product?.object?.overrides?.name && product?.object?.name !== product?.object?.overrides?.name && (
                            <Popup
                              trigger={<Icon name="warning circle" color="yellow"/>}
                              content={t('item_edit.override')}
                            />
                          )}
                      </div>


                      {product.object.description && (
                        <div css={css`
                            grid-area: description;
                            display: flex;
                            flex-direction: column;
                            gap: 1rem;
                        `}>
                            <div css={css`
                                color: #555;
                                line-height: 1.7;
                                font-size: 1em;
                                display: flex;
                                align-items: flex-start;
                                gap: 8px;
                            `}>
                                        <span css={css`
                                            text-align: justify;
                                            margin-right: 4px;
                                        `}>
                                            {product.object.description}
                                        </span>
                                {product?.object?.overrides?.description && product?.object?.description !== product?.object?.overrides?.description && (
                                  <Popup
                                    trigger={<Icon name="warning circle" color="yellow"/>}
                                    content={t('item_edit.override')}
                                  />
                                )}
                            </div>
                        </div>
                      )}

                      {itemHasTags && (
                        <div
                          css={css`
                              grid-area: tags;
                              display: flex;
                              flex-wrap: wrap;
                              gap: .5rem;
                              align-content: center;
                          `}
                        >
                            <CountriesOfOriginLabel item={product} />
                            {product.containsAlcohol && <Label>{t('product_details.contains_alcohol')}</Label>}
                        </div>
                      )}

                      <div css={css`
                          grid-area: pricing;
                          display: flex;
                          flex-direction: row;
                          gap: 5rem;
                      `}>
                          <div
                            css={css`
                                display: flex;
                                flex-direction: column;
                                flex-grow: 0.1;
                                gap: .5rem;
                            `}
                          >
                              <ProductPrices product={product}/>
                          </div>
                          <div
                            css={css`
                                display: flex;
                                flex-direction: column;
                                flex-grow: 0.1;
                            `}
                          >
                              <UltyInputText
                                readonly
                                label={t('item_edit.taxRate')}
                                value={`${product.taxRate} %`}
                              />

                              {product.inventory?.externalId &&
                                <UltyInputText
                                  readonly={true}
                                  label={`${t('product_details.sku')}:`}
                                  value={product.inventory.externalId}
                                />}
                          </div>
                      </div>

                      <div css={css`
                          grid-area: identifiers;
                      `}>
                          <div css={css`
                              display: flex;
                              justify-self: center;
                              align-items: center;
                              column-gap: .5rem;
                          `}>
                              {validate(product.object.barcode) ?
                                (
                                  <div>
                                      <UltyInputText
                                        readonly={true}
                                        label={t('product_details.ean')}
                                      />
                                      <Barcode
                                        value={product.object.barcode}
                                        format={product.object.barcode.length === 8 ? 'EAN8' :
                                          product.object.barcode.length === 13 ? 'EAN13' :
                                            undefined}
                                        width={1.5}
                                        height={50}
                                      />
                                  </div>
                                ) :
                                <UltyInputText
                                  readonly={true}
                                  label={t('product_details.ean')}
                                  value={product.object.barcode}
                                />}
                              {product.object.barcode && (
                                <Button
                                  icon
                                  basic
                                  onClick={() => {
                                      navigator.clipboard.writeText(product.object.barcode)
                                  }}
                                >
                                    <Icon name="copy"/>
                                </Button>
                              )}
                          </div>
                      </div>
                  </div>

                  {(isProviderAPos(user.provider) && product?.id) && (
                    <div css={css`
                        margin-top: 24px;
                    `}>
                        <ProductSyncState product={product}/>
                    </div>
                  )}
              </div>
          </Segment>

          <Tab menu={{secondary: true, pointing: true}} panes={[
              (product.id && {
                  menuItem: t('item_categories.header'),
                  render: () => <Tab.Pane attached={false} loading={loading}><ItemCategories itemId={product.id}
                                                                                             canEdit={can('UPDATE', 'product')}/></Tab.Pane>,
              }),
              {
                  menuItem: t('product_details.modifiers.header'),
                  render: () => <ProductDetailsModifiers product={product} canEdit={canEditItem}/>,
              },
          ]}/>
      </>
    )
}

export default ProductDetails
